// src/features/campus-guesser/services/storageService.js
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const storage = getStorage();

/**
 * Upload an image file to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} locationId - Unique identifier for the location
 * @returns {Promise<{storagePath: string, downloadUrl: string}>}
 */
export async function uploadLocationImage(file, locationId) {
  try {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 5MB.');
    }

    // Create storage path: campus-images/{locationId}/{timestamp}-{filename}
    const timestamp = Date.now();
    const fileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_'); // Sanitize filename
    const storagePath = `campus-images/${locationId}/${timestamp}-${fileName}`;

    // Create storage reference
    const storageRef = ref(storage, storagePath);

    // Upload file with metadata
    const metadata = {
      contentType: file.type,
      customMetadata: {
        locationId: String(locationId),
        uploadedAt: new Date().toISOString()
      }
    };

    const snapshot = await uploadBytes(storageRef, file, metadata);
    console.log('✅ Image uploaded successfully:', storagePath);

    // Get download URL
    const downloadUrl = await getDownloadURL(snapshot.ref);

    return {
      storagePath,
      downloadUrl
    };
  } catch (error) {
    console.error('❌ Error uploading image:', error);
    throw error;
  }
}

/**
 * Delete an image from Firebase Storage
 * @param {string} storagePath - The storage path of the image to delete
 * @returns {Promise<void>}
 */
export async function deleteLocationImage(storagePath) {
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    console.log('✅ Image deleted successfully:', storagePath);
  } catch (error) {
    console.error('❌ Error deleting image:', error);
    throw error;
  }
}

/**
 * Get a fresh download URL for an existing storage path
 * @param {string} storagePath - The storage path of the image
 * @returns {Promise<string>}
 */
export async function getImageUrl(storagePath) {
  try {
    const storageRef = ref(storage, storagePath);
    const downloadUrl = await getDownloadURL(storageRef);
    return downloadUrl;
  } catch (error) {
    console.error('❌ Error getting image URL:', error);
    throw error;
  }
}
